import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { filter, skip, take } from 'rxjs';
import { ProductService } from '../product.service';
import { ProductCost } from './product-costs.model';
import { ProductCostMethodLabelPipe } from './pipes/product-cost-method-label.pipe';
import { ProductCostStatusLabelPipe } from './pipes/product-cost-status-label.pipe';
import { ProductCostAddDialogComponent } from './product-cost-add-dialog/product-cost-add-dialog.component';
import { ProductCostManageDialogComponent } from './product-cost-manage-dialog/product-cost-manage-dialog.component';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductCostTimelineComponent } from './product-cost-timeline/product-cost-timeline.component';
import {
	firstDayOfCurrentMonth,
	lastDayOfCurrentMonth,
	normalizeDateRangeEnd,
	startOfDay,
} from './product-cost-timeline/product-cost-timeline.utils';
import {
	MatDateRangeInput,
	MatDateRangePicker,
	MatDatepickerToggle,
	MatEndDate,
	MatStartDate,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';

@Component({
	selector: 'app-product-costs',
	standalone: true,
	imports: [
		MatTableModule,
		MatButtonModule,
		ProductCostMethodLabelPipe,
		ProductCostStatusLabelPipe,
		MatTooltipModule,
		ProductCostTimelineComponent,
		ReactiveFormsModule,
		MatDateRangeInput,
		MatDateRangePicker,
		MatDatepickerToggle,
		MatStartDate,
		MatEndDate,
		MatFormFieldModule,
		MatInputModule,
	],
	templateUrl: './product-costs.component.html',
	styleUrl: './product-costs.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
})
export class ProductCostsComponent implements OnInit {
	readonly displayedColumns = [
		'name',
		'status',
		'kind',
		'currency',
		'paymentMethod',
		'method',
		'fixedCapacity',
		'isFaculty',
		'note',
		'actions',
	] as const;

	readonly productCosts = signal<ProductCost[]>([]);
	readonly expandedElement = signal<ProductCost | null>(null);
	readonly rangeStart = signal<Date>(firstDayOfCurrentMonth());
	readonly rangeEnd = signal<Date>(lastDayOfCurrentMonth());

	readonly rangeForm = new FormGroup({
		start: new FormControl<Date | null>(firstDayOfCurrentMonth()),
		end: new FormControl<Date | null>(lastDayOfCurrentMonth()),
	});

	private readonly productService = inject(ProductService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);
	private readonly infobox = inject(InfoboxService);
	private readonly dateAdapter = inject(DateAdapter);

	ngOnInit(): void {
		this.dateAdapter.setLocale('pl-PL');
		this.rangeForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => {
			if (v.start) {
				this.rangeStart.set(startOfDay(v.start));
			}
			if (v.end) {
				this.rangeEnd.set(normalizeDateRangeEnd(v.end));
			}
		});
		const initialStart = this.rangeForm.value.start ?? firstDayOfCurrentMonth();
		const initialEnd = this.rangeForm.value.end ?? lastDayOfCurrentMonth();
		this.rangeStart.set(startOfDay(initialStart));
		this.rangeEnd.set(normalizeDateRangeEnd(initialEnd));
		this.productService.dataLoaded$.pipe(skip(1), take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const costs = this.productService.data?.costs ?? [];
			this.productCosts.set(costs);
		});
	}

	isExpanded(element: ProductCost): boolean {
		return this.expandedElement() === element;
	}

	toggle(element: ProductCost): void {
		this.expandedElement.set(this.isExpanded(element) ? null : element);
	}

	openAddCostDialog(): void {
		const productId = this.productService.data?.id;
		if (productId === null) {
			return;
		}
		const dialogRef = this.dialog.open(ProductCostAddDialogComponent, {
			disableClose: true,
			autoFocus: false,
			data: { productId },
		});
		dialogRef.afterClosed().subscribe(() => {
			const costs = this.productService.data?.costs ?? [];
			this.productCosts.set(costs);
		});
	}

	openEditCostDialog(cost: ProductCost): void {
		const productId = this.productService.data?.id;
		if (productId === null) {
			return;
		}
		const dialogRef = this.dialog.open(ProductCostAddDialogComponent, {
			disableClose: true,
			autoFocus: false,
			data: { productId, cost },
		});
		dialogRef.afterClosed().subscribe(() => {
			const costs = this.productService.data?.costs ?? [];
			this.productCosts.set(costs);
		});
	}

	openManageCostDialog(cost: ProductCost): void {
		const dialogRef = this.dialog.open(ProductCostManageDialogComponent, {
			disableClose: true,
			autoFocus: false,
			data: { cost },
			panelClass: 'product-cost-manage-dialog-panel',
		});
		dialogRef
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => {
				const costs = this.productService.data?.costs ?? [];
				this.productCosts.set(costs);
				this.infobox.success('Pomyślnie zapisano');
			});
	}
}
